/**
 * 이상한 방 - 시점 줄 ("OO 시점" 전환 버튼 + 그 사람의 최근 발견 티커, 순수 DOM)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제 - 탄환이 다 떨어지면 관전) · 5절(웃김 설계 포인트 - 관전자도 화면 안에)
 *            `이상한방-상태모델-v0.1.md` 11.3절(카메라 추종은 클라이언트의 것) · 원칙 2(UI가 덮은 면적은 곧 볼 수 없는 방의 넓이)
 *            로드맵 M5(`spectateBar` - "OO 시점" 전환 버튼, 최근 발견 티커. 카메라는 대상의 발견 위치를 따라간다)
 *
 * ## 탄환이 다 떨어진 사람의 죽은 시간을 콘텐츠로
 *
 * 관전은 탈락이 아니다(GLOSSARY 1.5). 방은 계속 보이고 쏘지만 못하는데, 그 사람에게 남은 즐거움은 **남이 찾는 것을 보는 것**이다.
 * 그래서 이 줄은 관전 중일 때만 서고, 대상을 고르면 카메라가 그 사람의 발견 자리로 따라가며(그 배선은 `game/matchSessionParty.ts`),
 * 그 사람이 찾은 것이 여기 티커에 한 줄씩 쌓인다. 봇에게는 조준 신호(presence)가 없으므로 따라갈 수 있는 것은 **발견 위치**뿐이고,
 * 사람 술래도 같은 신호로 따라간다 - 봇과 사람이 다른 코드 경로를 타지 않게(상태모델 9.3절).
 *
 * ## 누구를 세울지는 뷰가 정한다
 *
 * 관전 대상은 관전 중이 아닌 술래만이다(M5 규칙 2 - 관전자를 따라가면 빈 화면을 본다). 그 판단은 리듀서(`matchShoot.canSpectate`)의
 * 것이고 호스트가 `PublicPlayer.spectatable`로 내린다. 이 줄은 받은 목록(`targets`)을 그대로 버튼으로 세울 뿐이다 - 여기서
 * `spectating`·`role`을 읽어 목록을 걸러 내기 시작하면 규칙이 화면에 두 벌째로 생긴다(`playerStrip.ts`와 같은 규약).
 * 관전 중이던 대상이 관전이 되면 호스트가 그 사람의 `spectatable`을 거짓으로 내리고 내 `spectateTarget`을 풀어 준다 - 버튼이
 * 사라지는 것과 `watched`가 꺼지는 것 둘 다 뷰의 값이다.
 *
 * ## 티커는 대상의 발견만 담는다
 *
 * 밀어 넣는 것은 세션(`pushFind`)이고, 세션은 내가 따라가는 사람의 `foundChange`만 넣는다. 대상이 바뀌면(`watched`인 버튼이 달라진다)
 * 티커를 비운다 - 앞 사람의 발견이 새 사람의 것으로 읽히면 "저 사람이 저걸 찾았다"가 거짓이 된다. 줄은 최근 셋만 남긴다. 타이머가
 * 없다 - 발견은 사건이지만 티커는 기록이고, 기록은 다음 기록이 밀어낸다(`revealParty.ts`와 같은 결정).
 *
 * ## 자리
 *
 * 오른쪽 아래, 원래 방 버튼이 서는 자리다. 관전자에게는 그 버튼이 없으므로(`matchView.originStatusOf` - `unavailable`이면 감춘다)
 * 이 줄이 그 자리를 이어받는다 - 관전이 되는 순간 "쏘는 사람의 계기판"이 "보는 사람의 계기판"으로 바뀌는 그림이다.
 * 버튼은 44px(누르는 것). 색은 전부 `--gh-*`.
 */

import { t } from '../i18n'

import { createElement, setAttr, setFlag, setHidden, setText } from './dom'
import { publishSize } from './overlayLayout'

import './spectateBar.css'

/* --- 상수 ---------------------------------------------------------------------- */

/** 티커에 남기는 줄 수. 셋이면 "방금 · 그 전 · 그 전전"까지 - 그 이상은 기록이 아니라 목록이다. */
export const TICKER_MAX_LINES = 3

/* --- 타입 ---------------------------------------------------------------------- */

/** 시점 버튼 하나. 목록은 호스트가 `spectatable`로 내린 것 그대로다(`matchSessionView.buildSpectateBarView`). */
export type SpectateTarget = Readonly<{
  id: string
  name: string
  /** 내가 지금 이 사람을 따라가고 있는가(`SelfView.spectateTarget`). */
  watched: boolean
}>

export type SpectateBarView = Readonly<{
  /** 내가 관전 중인가(`SelfView.spectating`). 거짓이면 줄째 숨는다. */
  spectating: boolean
  targets: readonly SpectateTarget[]
}>

/** 티커 한 줄의 재료. 이름은 사람, 물건은 카탈로그의 이름이다 - 둘 다 사전을 거치지 않는다. */
export type SpectateFind = Readonly<{ name: string; item: string }>

export type SpectateBarHandle = Readonly<{
  root: HTMLElement
  render: (view: SpectateBarView) => void
  /** 따라가는 사람의 발견 한 줄. 세션이 그 사람의 `foundChange`에만 부른다. */
  pushFind: (find: SpectateFind) => void
  /** 배치 모드·잠든 무대에서는 줄째 접는다(HUD의 `setVisible`과 같은 시점). */
  setVisible: (visible: boolean) => void
  dispose: () => void
}>

export type SpectateBarOptions = Readonly<{
  mount: HTMLElement
  /**
   * 버튼을 눌렀다. 따라가던 사람을 다시 누르면 `null`(아무도 안 따라간다). 가부 판단은 호스트다 - 눌렀으면 언제나 부른다
   * (`gameHud.ts`의 탄창 버튼과 같은 규약).
   */
  onPick: (targetId: string | null) => void
}>

/* --- 본체 ---------------------------------------------------------------------- */

export function createSpectateBar(options: SpectateBarOptions): SpectateBarHandle {
  const root = createElement('div', 'sb-root')
  root.setAttribute('role', 'group')
  root.hidden = true

  const ticker = createElement('ol', 'sb-ticker')
  ticker.setAttribute('role', 'log')
  const buttons = createElement('div', 'sb-buttons')
  root.append(ticker, buttons)
  options.mount.append(root)

  /*
   * 줄의 높이를 내보낸다. 이 줄은 HUD 하단 줄과 **같은 자리**를 번갈아 쓰고(관전자에게는 원래 방 버튼이 없다),
   * 위에 서는 카메라 묶음은 둘 중 큰 쪽을 비켜야 한다. 이웃이 일곱이면 버튼이 두 줄이 되어 하단 줄보다 높아진다.
   */
  const size = publishSize(root, { height: '--sb-height' })

  /** 좌석 id → 버튼. 목록이 달라질 때만 다시 세운다(프레임마다 불리는 경로다). */
  let nodes = new Map<string, HTMLButtonElement>()
  let order: readonly string[] = []
  /** 지금 따라가는 사람. 바뀌면 티커를 비운다(머리말). */
  let watchedId: string | null = null
  const lines: SpectateFind[] = []

  function sameOrder(next: readonly SpectateTarget[]): boolean {
    if (next.length !== order.length) return false
    return next.every((target, index) => target.id === order[index])
  }

  function createButton(target: SpectateTarget): HTMLButtonElement {
    const button = createElement('button', 'sb-target')
    button.type = 'button'
    button.dataset['target'] = target.id
    button.addEventListener('click', () => {
      // 누른 것이 지금 따라가는 사람이면 풀고, 아니면 그 사람이다. 판단은 뷰의 `watched` 하나 - 여기서 상태를 따로 들지 않는다.
      options.onPick(watchedId === target.id ? null : target.id)
    })
    return button
  }

  function rebuild(next: readonly SpectateTarget[]): void {
    const fresh = new Map<string, HTMLButtonElement>()
    for (const target of next) fresh.set(target.id, nodes.get(target.id) ?? createButton(target))
    nodes = fresh
    order = next.map((target) => target.id)
    buttons.replaceChildren(...next.map((target) => fresh.get(target.id) ?? createButton(target)))
  }

  function paintTicker(): void {
    ticker.replaceChildren(
      ...lines.map((line) => createElement('li', 'sb-line', t('spectate.ticker.found', { name: line.name, item: line.item }))),
    )
    setHidden(ticker, lines.length === 0)
  }

  function clearTicker(): void {
    if (lines.length === 0) return
    lines.length = 0
    paintTicker()
  }

  return {
    root,

    render(view: SpectateBarView): void {
      setHidden(root, !view.spectating)
      size.refresh()
      if (!view.spectating) {
        // 관전이 끝났다(다음 라운드 복귀). 지난 라운드의 티커가 다음 관전에 이어지면 안 된다.
        watchedId = null
        clearTicker()
        return
      }
      setAttr(root, 'aria-label', t('spectate.bar.aria'))
      if (!sameOrder(view.targets)) rebuild(view.targets)
      const nextWatched = view.targets.find((target) => target.watched)?.id ?? null
      if (nextWatched !== watchedId) {
        watchedId = nextWatched
        clearTicker()
      }
      for (const target of view.targets) {
        const button = nodes.get(target.id)
        if (button === undefined) continue
        setText(button, t('spectate.target', { name: target.name }))
        setFlag(button, 'is-on', target.watched)
        setAttr(button, 'aria-pressed', target.watched ? 'true' : 'false')
      }
    },

    pushFind(find: SpectateFind): void {
      lines.unshift(find)
      if (lines.length > TICKER_MAX_LINES) lines.length = TICKER_MAX_LINES
      paintTicker()
      size.refresh()
    },

    setVisible(visible: boolean): void {
      // 접을 때만 손댄다. 펼 때는 다음 `render`가 관전 여부로 다시 정한다 - 여기서 펴면 관전이 아닌데 줄이 서는 프레임이 생긴다.
      if (!visible) setHidden(root, true)
      size.refresh()
    },

    dispose(): void {
      size.dispose()
      root.remove()
    },
  }
}
